import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { ConditionPresetsContext } from "@/context/ConditionPresetsContext";
import {
  ConditionPreset,
  SaveConditionPresetRequest
} from "@/components/Expressions/conditionPresetTypes";

/**
 * Provides saved condition-group presets for the signed-in site.
 * Failure modes: initial fetch failure resets to []. Subsequent refetch
 * failures preserve the existing array.
 */
export function ConditionPresetsProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const [presets, setPresets] = useState<ConditionPreset[]>([]);
  const [loading, setLoading] = useState(false);
  const [everLoaded, setEverLoaded] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.get("ConditionPresets");
      const data = (await res.json()) as ConditionPreset[];
      setPresets(Array.isArray(data) ? data : []);
      setEverLoaded(true);
    } catch (e) {
      if (!everLoaded) setPresets([]);
      // else: preserve existing data on transient refetch failure
      // eslint-disable-next-line no-console
      console.warn("[ConditionPresets] reload failed (non-fatal):", e);
    } finally {
      setLoading(false);
    }
  }, [api.admin, everLoaded]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = useCallback(
    async (req: SaveConditionPresetRequest) => {
      try {
        const res = await api.admin.post("ConditionPresets", req);
        if (!res.ok) return null;
        const created = (await res.json()) as ConditionPreset;
        setPresets((prev) =>
          [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
        );
        return created;
      } catch (e) {
        console.warn("[ConditionPresets] create failed:", e);
        return null;
      }
    },
    [api.admin]
  );

  const update = useCallback(
    async (id: number, req: SaveConditionPresetRequest) => {
      try {
        const res = await api.admin.put(`ConditionPresets/${id}`, req);
        if (!res.ok) return null;
        const updated = (await res.json()) as ConditionPreset;
        setPresets((prev) =>
          prev
            .map((p) => (p.id === id ? updated : p))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        return updated;
      } catch (e) {
        console.warn("[ConditionPresets] update failed:", e);
        return null;
      }
    },
    [api.admin]
  );

  const remove = useCallback(
    async (id: number) => {
      try {
        const res = await api.admin.delete(`ConditionPresets/${id}`);
        if (!res.ok) return false;
        setPresets((prev) => prev.filter((p) => p.id !== id));
        return true;
      } catch (e) {
        console.warn("[ConditionPresets] delete failed:", e);
        return false;
      }
    },
    [api.admin]
  );

  return (
    <ConditionPresetsContext.Provider
      value={{ presets, loading, reload, create, update, remove }}
    >
      {props.children}
    </ConditionPresetsContext.Provider>
  );
}

export function useConditionPresets() {
  return React.useContext(ConditionPresetsContext);
}
